import React, { useEffect, useCallback, useState } from 'react';
import SuccessMessage from './SuccessMessage';
import NewReportContent from './NewReportContent';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import { Program } from '../../Reports/NewReportDialog';
import { useGoToReport } from './useGoToReport';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  SuccessResponse,
  ReportsForProgram,
  ReportingType,
  createNewReport,
  getReportsForProgram,
  cloneReport,
} from './api';
import { useClient } from 'shared/apollo';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { adjustStartAndEndDates, getReportIdForProgram } from './utils';
import { reloadPage } from 'shared/utils/pageUtils';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

interface NewReportProps {
  open: boolean;
  existingProgram?: Program;
  existingYear?: number;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  onClose: () => unknown;
  handleReportCountLoad: (() => unknown) | undefined;
}

export const getDefaultProgram = (
  defaultProgramName: string | undefined,
  programsList: RequestState<{
    allPrograms: Program[];
  }>,
): Program | undefined => {
  if (!defaultProgramName) return undefined;
  return programsList.data?.allPrograms.find(
    program => program.name === defaultProgramName,
  );
};

const NewReport: React.FC<NewReportProps> = ({
  open,
  programsList,
  existingYear,
  existingProgram,
  onClose,
  handleReportCountLoad,
}) => {
  const client = useClient();
  const { isThirdParty } = useUserActions();
  const goToReport = useGoToReport();
  const [reportsForProgram, makeReportsForProgramRequest] = useGraphQLRequest<{
    reports: ReportsForProgram[];
  }>();
  const [newReport, makeNewReportRequest, setNewReport] = useGraphQLRequest<
    SuccessResponse<'newReport'>
  >();
  const [existing, setExisting] = useState<{
    year: number | undefined;
    program: Program | undefined;
  }>({
    year: existingYear,
    program: existingYear ? existingProgram : undefined,
  });
  const [selected, setSelected] = useState<{
    year: number | undefined;
    program: Program | undefined;
  }>({
    year: undefined,
    program: existingProgram,
  });

  const [reportingType, setReportingType] = useState<ReportingType>('FULL');
  const [showNext, setShowNext] = useState<boolean>(Boolean(existingProgram));
  const [cloneType, setCloneType] = useState<
    | 'existing-report'
    | 'new-report'
    | 'another-program-report'
    | 'success'
    | undefined
  >(
    existingYear
      ? 'existing-report'
      : existingProgram
      ? 'new-report'
      : undefined,
  );

  useEffect(() => {
    if (newReport.type !== 'REQUEST_SUCCESS') return;
    setCloneType('success');
    handleReportCountLoad?.();
  }, [newReport.type, setCloneType]);

  useEffect(() => {
    if (!client) return;
    reportsForProgram.type === 'REQUEST_INIT' &&
      makeReportsForProgramRequest(getReportsForProgram(client));
  }, [client]);

  const handleGoToReport = useCallback(() => {
    if (newReport.type !== 'REQUEST_SUCCESS') return;
    const reportId = (newReport.data.newReport as {
      id: number;
      success: true;
    }).id;
    goToReport({
      reportId,
      programId: selected.program?.Id,
      programName: selected.program?.name,
      reportingType,
      lightMode: 'in-place',
    });
  }, [newReport, selected.program, reportingType, goToReport]);

  const handleBack = useCallback(() => {
    setShowNext(false);
  }, [setShowNext]);

  const handleNext = useCallback(() => {
    setShowNext(true);
  }, [setShowNext]);

  const handleCreateReport = useCallback(() => {
    if (!client || !selected.program || !selected.year) return;
    const report =
      existing.program && existing.year
        ? getReportIdForProgram(
            reportsForProgram.data?.reports || [],
            existing.program,
            existing.year,
          )
        : undefined;

    const dates = adjustStartAndEndDates(
      report?.startDate,
      report?.endDate,
      selected.year,
    );
    if (report?.reportId) {
      makeNewReportRequest(
        cloneReport(
          client,
          report.reportId,
          selected.program,
          selected.year,
          dates.startDate,
          dates.endDate,
          undefined,
          reportingType,
        ),
      );
    } else {
      makeNewReportRequest(
        createNewReport(
          client,
          selected.program,
          selected.year,
          existing.program,
          existing.year,
          reportingType,
          isThirdParty ? 'PUBLIC' : undefined,
        ),
      );
    }
  }, [
    client,
    existing,
    selected,
    reportsForProgram,
    makeNewReportRequest,
    reportingType,
    isThirdParty,
  ]);

  return (
    <TrueImpactModal
      title="New Report"
      open={open}
      showExpandButton={true}
      onClose={onClose}
      maxWidth="1000px"
      initialExpandValue={isThirdParty}
      previous={{
        onClick: cloneType === 'success' || !showNext ? reloadPage : handleBack,
        text:
          cloneType === 'success' ? 'No thanks' : showNext ? 'Back' : 'Cancel',
        disabled: false,
        isLoading: false,
      }}
      next={{
        onClick:
          cloneType === 'success'
            ? handleGoToReport
            : showNext
            ? handleCreateReport
            : handleNext,
        text:
          cloneType === 'success'
            ? 'Yes, take me there'
            : showNext
            ? 'Create Report'
            : 'Next',
        disabled:
          cloneType === 'success'
            ? false
            : showNext
            ? cloneType === 'new-report'
              ? !selected.year || !selected.program
              : !existing.year ||
                !existing.program ||
                !selected.year ||
                !selected.program
            : !cloneType,
        isLoading: newReport.type === 'REQUEST_START',
      }}
    >
      {cloneType === 'success' ? (
        <SuccessMessage
          heading="Your report was successfully created!"
          subHeading={{
            one: 'Would you like to begin updating your report now?',
            two: 'You can always come back later to edit.',
          }}
        />
      ) : (
        <NewReportContent
          cloneType={cloneType}
          existing={existing}
          programsList={programsList}
          reportsForProgram={reportsForProgram}
          selected={selected}
          setCloneType={setCloneType}
          setExisting={setExisting}
          setSelected={setSelected}
          showNext={showNext}
          reportingType={reportingType}
          setReportingType={setReportingType}
        />
      )}
    </TrueImpactModal>
  );
};

export default NewReport;
